import { Address, IInsurer } from './insurer.model';
import { IAddressForm, IBranch } from './branches.model';
import {
  BrokerType,
  IBankDetail,
  IBrokerRevenueRate,
  IntermediaryStatusType,
  IntermediaryType,
  PolicyStatus,
} from './broker.model';
import { ClientStatus, ClientType, IBankForm } from './clients.models';
import { ICommissionRate } from './commission';
import { IQueryParams } from './queryparams';
import {
  IRepresentative,
  IRepresentativeForm,
  IRepresentativeRequest,
} from './representative.model';
import { FormControl, FormGroup } from '@angular/forms';
import { IBaseModel } from './base.model';
import { Currency } from './currency';
import { PaymentMethod } from './payment.model';
import { IMotorLimit } from './motor-product.model';

export interface IInsurerCode {
  insurerId: number;
  code: string;
}

export interface IIntermediary extends IBaseModel {
  name: string;
  logoUrl: string;
  taxPayerId: string;
  email: string;
  address?: Address;
  bankDetail?: IBankDetail;
  branches: IBranch[];
  status: string;
  representatives?: IRepresentative[];
  insurers?: IInsurer[];
  type: IntermediaryType;
  commissionRates: ICommissionRate[];
  clients: IIntermediaryClient[];
  policies: IIntermediaryPolicy[];
  paymentMethods: PaymentMethod[];
  premiumLimits: IMotorPremiumLimit[];
  brokerType?: BrokerType;
  smsOriginatorId?: string;
  systemPermissions: SystemPermissions[];
  revenueRates: IBrokerRevenueRate[];
  insurerIntermediaryBranches: InsurerIntermediaryBranch[];
  insurerCodes?: IInsurerCode[] | null;
}

export interface IIntermediaryClient extends IBaseModel {
  email: string;
  phoneNumber: string;
  physicalAddress: string;
  type: ClientType;
  status: ClientStatus;
}

export interface IIntermediaryPolicy extends IBaseModel {
  policyNumber: string;
  status: PolicyStatus;
  approvedDate: string;
  startDate: string;
  endDate: string;
  currency: Currency;
  premiumLevy: number;
  basicPremium: number;
  grossPremium: number;
}

export enum IdType {
  Nrc = 'Nrc',
  Passport = 'Passport',
  DrivingLicense = 'Driving License',
}

export interface IIntermediaryUpdateResponse extends IBaseModel {
  name: string;
  logoUrl: string;
  taxPayerId: string;
  email: string;
  address: Address;
  bankDetail: IBankDetail;
  // branches: BranchResponse[] | BranchResponse;
}

export interface IIntermediaryStats {
  totalAgents: number;
  totalBrokers: number;
  totalActiveAgents: number;
  totalActiveBrokers: number;
}

export interface IIntermediaryRequest {
  name: string;
  logoUrl?: string;
  taxPayerId: string;
  email: string;
  smsOriginatorId?: string;
  address: Partial<Address>;
  bankDetail: Partial<IBankDetail>;
  representative: Partial<IRepresentativeRequest>;
  paymentMethods: PaymentMethod[];
  insurerId?: number | null;
  branchId?: number | null;
  type: IntermediaryType;
  systemPermissions: SystemPermissions[];
  revenueRates?: IBrokerRevenueRate[];
  insurerCodes?: IInsurerCode[] | null;
}

export interface IIntermediaryForm {
  name: FormControl<string>;
  taxPayerId: FormControl<string>;
  email: FormControl<string>;
  smsOriginatorId: FormControl<string>;
  type: FormControl<IntermediaryType>;
  paymentMethods: FormControl<PaymentMethod[]>;
  address: FormGroup<IAddressForm>;
  representative: FormGroup<IRepresentativeForm>;
  bankDetail: FormGroup<IBankForm>;
}

export interface IIntermediaryUpdateForm {
  name: FormControl<string>;
  taxPayerId: FormControl<string>;
  email: FormControl<string>;
  address: FormGroup<IAddressForm>;
  paymentMethods: FormControl<PaymentMethod[]>;
  bankDetail: FormGroup<IBankForm>;
}

export interface IIntermediaryStatistics {
  totalClients: number;
  totalPolicies: number;
  earnedCommissionStats: IIntermediaryCommissionStats;
  paidCommissionStats: IIntermediaryCommissionStats;
  totalCommissionPaid: number;
  premiumStats: IPremiumStats;
}

export interface IIntermediaryCommissionStats {
  totalCommission: number;
  dailyCommission: number;
  weeklyCommission: number;
  monthlyCommission: number;
  yearlyCommission: number;
}

export interface IPremiumStats {
  totalGWP: number;
  dailyPremium: number;
  weeklyPremium: number;
  monthlyPremium: number;
  yearlyPremium: number;
}

export interface IIntermediaryQueryParams extends IQueryParams {
  type?: string;
  query?: string | null;
  insurerId?: number | null;
  partnerId?: number | null;
  broker?: number | null;
  IntermediaryStatuses?: IntermediaryStatusType[] | null;
}

export interface IIntermediaryResponse {
  result: any;
  name: string | null;
  taxPayerId: string | null;
  email: string | null;
  address?: Address | null;
  bankDetail?: IBankDetail | null;
  branches: IBranch[] | null;
  representatives?: IRepresentative[] | null;
  type: IntermediaryType | null;
  commissionRates: ICommissionRate[] | null;
  value: IIntermediary;
}

export type ICommissionWalletTransactionType = 'Withdraw' | 'Deposit';
export type IStatus = 'Active' | 'Archived';

export interface ICommissionWalletParams extends IQueryParams {
  transactionType?: ICommissionWalletTransactionType;
}

export interface IPremiumLimit {
  intermediaryId?: number | null;
  productId: number;
  partnerId?: number | null;
  addonLimits?: IAddonLimit;
}

export interface IMotorPremiumLimit extends IPremiumLimit {
  comprehensiveLimits?: ComprehensiveMotorPremiumLimit;
  thirdPartyLimits?: IMotorLimit[];
}

export const defaultIntermediaryMotorPremiumLimit: IMotorPremiumLimit =
  {
    intermediaryId: 0,
    partnerId:0,
    productId: 0,
    addonLimits: {
      minDiscountRate: 0,
      maxDiscountRate: 0,
      maxExtensionRate: 0,
      minExtensionRate: 0,
    },
    comprehensiveLimits: {
      maxPremiumRate: 0,
      maxSumInsured: 0,
      minPremiumRate: 0,
      minSumInsured: 0,
      minSumInsuredAmount: 0,
      maxSumInsuredAmount: 0,
    },
  };

export interface ComprehensiveMotorPremiumLimit {
  maxSumInsured: number;
  minSumInsured: number;
  maxPremiumRate: number;
  minPremiumRate: number;
  minSumInsuredAmount: number;
  maxSumInsuredAmount: number;
}


export interface SystemPermissions {
  insurerId: number;
  canEditPolicyDates: boolean;
  canChangeThirdPartyLimits: boolean;
  canIssueComprehensiveNoPhotos: boolean;
  canEditPolicyVehicleDetails: boolean;
  canOverridePremium: boolean;
  canDoManualRtsaUpload: boolean;
  canExemptCommissionWithdrawalTax: boolean;
  canWithdrawCommission: boolean;
  canWithdrawDigitalPaymentOnly: boolean;
  canEditPartnerCommission: boolean;
}

export interface IAssignedPaymentMethods {
  insurer?: IInsurer | null;
  insurerId: number;
  brokerId?: number | null;
  partnerId?: number | null;
  paymentMethods: PaymentMethod[];
}

export interface IIntermediaryPaymentMethods extends IAssignedPaymentMethods {
  intermediaryId: number;
}

export interface IAddonLimit {
  minDiscountRate: number;
  maxDiscountRate: number;
  maxExtensionRate: number;
  minExtensionRate: number;
}

export interface InsurerIntermediaryBranch {
  insurerId: number;
  intermediaryId: number;
  branch: IBranch;
}

export interface ICommissionRateDeleteRequest{
  commissionRateId: number;
  partnerId?: number | null;
  intermediaryId?: number | null;
}
